import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { titleCaseLabel } from '../../lib/text';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  children: ReactNode;
}

// Calibrated Modern: solid blue primary (the only "tap me" colour),
// white-on-rule secondary, brand green for SUCCESS / "release" actions
// only (so the green doesn't compete with blue for action affordance),
// brand-red danger, transparent ghost for tertiary moves.
const variantClasses: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-white border-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] active:bg-[var(--color-primary-pressed)]',
  secondary:
    'bg-[var(--color-surface)] text-[var(--color-ink)] border-[var(--color-rule-strong)] hover:bg-[var(--color-surface-muted)] hover:border-[var(--color-primary)]',
  success:
    'bg-[var(--color-success)] text-white border-[var(--color-success)] hover:bg-[var(--color-success-hover)]',
  danger:
    'bg-[var(--color-danger)] text-white border-[var(--color-danger)] hover:brightness-95',
  ghost:
    'bg-transparent text-[var(--color-ink-muted)] border-transparent hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]',
};

// Touch-target safe sizes — md hits 40px, lg hits 48px (≥ Apple/Material 44).
const sizeClasses: Record<Size, string> = {
  sm: 'px-3 py-2 text-[13px] min-h-[36px]',
  md: 'px-5 py-2.5 text-sm min-h-[40px]',
  lg: 'px-7 py-3 text-[15px] min-h-[48px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      disabled,
      children,
      className = '',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        className={[
          'inline-flex items-center justify-center gap-2',
          'border font-semibold tracking-tight',
          'rounded-[4px]',
          'cursor-pointer',
          'transition-[background-color,color,border-color,box-shadow] duration-150 ease-out',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantClasses[variant],
          sizeClasses[size],
          className,
        ].join(' ')}
        {...rest}
      >
        {isLoading && (
          <span
            aria-hidden="true"
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
          />
        )}
        {/* Plain-string labels are Title-Cased centrally so every button reads
            consistently ("Save Changes", "Sign Out"). Buttons whose children
            are JSX (icon + text, dynamic nodes) are left untouched. */}
        {typeof children === 'string' ? titleCaseLabel(children) : children}
      </button>
    );
  }
);

Button.displayName = 'Button';
